// ============================================================================
// @orgazone/core-money — DOCTRINE NUMÉRIQUE partagée (SPEC §24.6bis A). UTILITÉ : les primitives
// fixed-point ENTIÈRES (BigInt en interne, ZÉRO flottant) utilisées par core-vat / core-pricing /
// core-inventory / core-loyalty / core-analytics → prix, coûts (PMP sous-centime), TVA, remises,
// gains et métriques calculés de façon DÉTERMINISTE et IDENTIQUE partout (caisse ↔ BO ↔ ERP).
//
// CONVENTIONS (l'échelle est la comptabilité de l'APPELANT) :
//  • Argent = centimes (échelle 2). Prix/coûts peuvent porter une échelle plus FINE (PMP, B2B < centime).
//  • Quantité = entier en « unité mineure », l'échelle est portée par l'UoM (pièce/kg-mg/m-mm/L-mL).
//  • UN SEUL arrondi commercial half-up (§30.3), au total de ligne / à l'affichage. Symétrique sur
//    les négatifs (retours/avoirs). Les PRODUITS INTERMÉDIAIRES passent par BigInt → jamais de
//    débordement. Les entrées sont gardées `Number.isSafeInteger` (FAIL-FAST < 2^53) : au-delà, on
//    LÈVE plutôt que de renvoyer un résultat silencieusement faux. Seul flottant TOLÉRÉ = `toMinor`
//    (frontière de SAISIE : une décimale utilisateur → entier) ; tout le reste est 100 % BigInt.
// ============================================================================

// Cœur des arrondis : |num|/den arrondi half-up en BigInt, ré-appliqué le signe (num signé, den > 0).
function roundBig(num: bigint, den: bigint): bigint {
  const neg = num < 0n;
  const n = neg ? -num : num;
  const q = n / den;
  const rem = n - q * den;
  const rounded = 2n * rem >= den ? q + 1n : q;
  return neg ? -rounded : rounded;
}

// ── divRoundHalfUp — LE seul arrondi commercial (§30.3) : (numerator/denominator) au plus proche entier ──
// Half-up (0,5 → 1), SYMÉTRIQUE sur les négatifs (retours : −0,5 → −1). Dénominateur > 0. BigInt interne.
export function divRoundHalfUp(numerator: number, denominator: number): number {
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator) || denominator <= 0) {
    throw new Error('core-money: divRoundHalfUp attend des entiers sûrs (< 2^53) et un dénominateur > 0');
  }
  return Number(roundBig(BigInt(numerator), BigInt(denominator)));
}

// ── mulDivRound — (a×b)/denominator arrondi half-up ──
// UTILITÉ : prix × qté / échelle, moyenne pondérée (PMP), pourcentage — le produit passe en BigInt donc
// aucun débordement même sur gros volumes B2B × prix fins. `a`/`b` signés, `denominator` > 0.
export function mulDivRound(a: number, b: number, denominator: number): number {
  if (!Number.isSafeInteger(a) || !Number.isSafeInteger(b) || !Number.isSafeInteger(denominator) || denominator <= 0) {
    throw new Error('core-money: mulDivRound attend des entiers sûrs (< 2^53) et un dénominateur > 0');
  }
  return Number(roundBig(BigInt(a) * BigInt(b), BigInt(denominator)));
}

// ── weightedAverage — moyenne pondérée Σ(wᵢ·vᵢ) / Σwᵢ, arrondi half-up ──
// UTILITÉ : PMP (coût moyen pondéré par la quantité — core-inventory), prix/taux mélangés, blend. Le
// numérateur Σ(wᵢ·vᵢ) passe en BigInt (aucun débordement) → PMP sous-centime SANS dérive (§24.6bis C).
// `Σ poids` doit être > 0 (moyenne définie) ; poids/valeurs signés autorisés (den > 0 garanti par la garde).
export function weightedAverage(weights: number[], values: number[]): number {
  if (weights.length !== values.length) throw new Error('core-money: weightedAverage attend autant de poids que de valeurs');
  let num = 0n;
  let den = 0n;
  for (let i = 0; i < weights.length; i++) {
    const w = weights[i] ?? 0;
    const v = values[i] ?? 0;
    if (!Number.isSafeInteger(w) || !Number.isSafeInteger(v)) throw new Error('core-money: weightedAverage attend des entiers sûrs (< 2^53)');
    num += BigInt(w) * BigInt(v);
    den += BigInt(w);
  }
  if (den <= 0n) throw new Error('core-money: weightedAverage attend une somme de poids > 0');
  return Number(roundBig(num, den));
}

// ── divFloor — division entière PLANCHER (vers −∞) ──
// UTILITÉ : contextes strictement entiers où l'on n'arrondit jamais au-dessus (ex. points de fidélité
// acquis = plancher). Dénominateur > 0.
export function divFloor(numerator: number, denominator: number): number {
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator) || denominator <= 0) {
    throw new Error('core-money: divFloor attend des entiers sûrs (< 2^53) et un dénominateur > 0');
  }
  // Plancher vers −∞ en BigInt (dén. > 0) : la division BigInt tronque vers 0, on retire 1 aux
  // négatifs à reste non nul. Supprime le dernier flottant du module (doctrine « ZÉRO flottant »).
  const N = BigInt(numerator);
  const D = BigInt(denominator);
  const q = N / D;
  return Number(N % D !== 0n && numerator < 0 ? q - 1n : q);
}

// ── applyBp — montant × points de base / 10000, arrondi half-up ──
// UTILITÉ : appliquer un taux SANS flottant (remise %, taux de conversion fidélité, TVA). 20 % = 2000 pb.
// `bp` non borné (un coefficient de marque > 100 % est légitime).
export function applyBp(amount: number, bp: number): number {
  return mulDivRound(amount, bp, 10000);
}

// ── applyBpChain — enchaîne PLUSIEURS facteurs en points de base avec UN SEUL arrondi ──
// UTILITÉ : appliquer des taux SUCCESSIFS sans arrondi intermédiaire — `amount × Π bp_i / 10000^n`,
// un seul half-up FINAL (§30.3). Ex. net d'une ligne = brut × (10000−remise_ligne) × (10000−remise_globale)
// / 10000² : les deux remises combinées AVANT l'arrondi (un double arrondi ferait diverger d'un centime).
// BigInt en interne : le produit dépasse 2^53 (c'est LA raison d'être de cette primitive vs `applyBp`).
// SOURCE UNIQUE de l'enchaînement de remises (consommée par core-pricing / le net de ligne des tickets)
// → plus aucune copie de la règle d'arrondi ailleurs. Chaîne vide → `amount`. `bp` entiers.
export function applyBpChain(amount: number, bps: number[]): number {
  if (!Number.isInteger(amount)) throw new Error('core-money: applyBpChain attend un montant entier');
  let num = BigInt(amount);
  let den = 1n;
  for (const bp of bps) {
    if (!Number.isInteger(bp)) throw new Error('core-money: applyBpChain attend des points de base entiers');
    num *= BigInt(bp);
    den *= 10000n;
  }
  return Number(roundBig(num, den));
}

// ── allocate — répartit `total` sur des `weights` au PRORATA, reliquat par PLUS-FORT-RESTE ──
// UTILITÉ : garantir Σ résultat = `total` EXACT (remise globale prorata, ventilation TVA, distribution
// de gains) — jamais de centime perdu. Gère les montants NÉGATIFS (retours) et les poids MIXTES. Σ poids = 0
// (tous nuls OU qui s'annulent) → prorata indéfini → repli parts ÉGALES. Plancher vers −∞ puis +1 aux plus
// forts restes (déterministe, index bas en cas d'égalité). `total`/`weights` = entiers sûrs ; [] + total≠0 → lève.
export function allocate(total: number, weights: number[]): number[] {
  if (!Number.isSafeInteger(total)) throw new Error('core-money: allocate attend un total entier sûr (< 2^53)');
  if (!weights.every((x) => Number.isSafeInteger(x))) throw new Error('core-money: allocate attend des poids entiers sûrs (< 2^53)');
  const n = weights.length;
  if (n === 0) {
    if (total !== 0) throw new Error('core-money: allocate ne peut pas répartir un total NON NUL sur AUCUN poids');
    return []; // rien à répartir → tableau vide (Σ = total = 0, contrat préservé)
  }
  let w = weights;
  let sumW = w.reduce((s, x) => s + x, 0);
  if (sumW === 0) {
    // Σ poids = 0 (tous nuls OU poids mixtes qui s'annulent, ex. [-1,2,-1]) → prorata INDÉFINI :
    // repli déterministe en parts ÉGALES (Σ = total préservée) — jamais un prorata des poids d'origine.
    w = w.map(() => 1);
    sumW = n;
  } else if (sumW < 0) {
    // Somme des poids NÉGATIVE (prorata sur des lignes de RETOUR) : on néglige poids ET somme.
    // Les ratios w[i]/sumW sont INCHANGÉS, mais S redevient > 0 → la division plancher + le reliquat
    // « plus-fort-reste » restent corrects (sinon `diff` devenait négatif et 1 unité était PERDUE).
    w = w.map((x) => -x);
    sumW = -sumW;
  }
  const T = BigInt(total);
  const S = BigInt(sumW);
  const base: number[] = [];
  const rems: bigint[] = []; // reste ∈ [0, S) par la division plancher
  let allocated = 0n;
  for (let i = 0; i < n; i++) {
    const prod = T * BigInt(w[i] ?? 0);
    let q = prod / S;
    let r = prod - q * S;
    if (r < 0n) {
      q -= 1n;
      r += S;
    } // plancher vers −∞ + reste positif
    base.push(Number(q));
    rems.push(r);
    allocated += q;
  }
  const diff = Number(T - allocated); // nb d'unités à redistribuer (∈ [0, n))
  const order = rems
    .map((r, i) => ({ r, i }))
    .sort((a, b) => (b.r === a.r ? a.i - b.i : b.r > a.r ? 1 : -1)); // reste décroissant, index bas d'abord
  for (let k = 0; k < diff; k++) {
    const idx = order[k]?.i ?? 0;
    base[idx] = (base[idx] ?? 0) + 1;
  }
  return base;
}

// ── rescale — convertit un entier d'une échelle décimale à une autre ──
// UTILITÉ : passer d'un PMP à échelle FINE (ex. 4 déc.) au centime (2 déc.) à l'affichage, ou l'inverse
// (exact quand on monte en précision, arrondi half-up quand on descend). Échelles = nombre de décimales ≥ 0.
export function rescale(value: number, fromScale: number, toScale: number): number {
  if (!Number.isSafeInteger(value) || !Number.isInteger(fromScale) || !Number.isInteger(toScale) || fromScale < 0 || toScale < 0) {
    throw new Error('core-money: rescale attend une valeur entière sûre et des échelles ENTIÈRES ≥ 0');
  }
  if (toScale === fromScale) return value;
  if (toScale > fromScale) return Number(BigInt(value) * 10n ** BigInt(toScale - fromScale));
  // Descente = un SEUL half-up ; dénominateur 10^k en BigInt (aucun flottant, exact quel que soit k).
  return Number(roundBig(BigInt(value), 10n ** BigInt(fromScale - toScale)));
}

// ── pctBp — ratio part/tout en POINTS DE BASE (contribution au CA, taux de marque) ──
// UTILITÉ : un pourcentage sans flottant. 45,00 % → 4500. `tout` ≤ 0 → 0 (garde : ratio non défini,
// jamais de crash en caisse). `part` signé (une marge peut être négative).
export function pctBp(part: number, whole: number): number {
  if (!Number.isSafeInteger(part) || !Number.isSafeInteger(whole)) throw new Error('core-money: pctBp attend des entiers sûrs (< 2^53)');
  if (whole <= 0) return 0;
  return mulDivRound(part, 10000, whole);
}

// ── ratioScaled — ratio num/den × 10^scale arrondi (décimales sans flottant) ──
// UTILITÉ : IDV = articles/tickets à 1 décimale (4,2 → 42, scale 1), vélocité (u/jour), prix moyen.
// `den` ≤ 0 → 0 (garde : jamais de division par zéro en caisse). `num` signé.
export function ratioScaled(numerator: number, denominator: number, scale: number): number {
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator) || !Number.isInteger(scale) || scale < 0) {
    throw new Error('core-money: ratioScaled attend des entiers sûrs et une échelle ENTIÈRE ≥ 0');
  }
  if (denominator <= 0) return 0;
  // num × 10^scale / den, half-up ; 10^scale en BigInt (exact quel que soit scale, aucun flottant).
  return Number(roundBig(BigInt(numerator) * 10n ** BigInt(scale), BigInt(denominator)));
}

// ── UoM — échelles d'unité mineure (unités mineures par unité MAJEURE) ──
// GÉNÉRIQUE et EXTENSIBLE (map, pas d'enum figé) : un nouveau corps de métier ajoute son unité sans
// refonte du cœur. Réf. SPEC §24.6bis A (retail à la pièce, CHR au poids/volume, ERP).
export const UOM_SCALE: Record<string, number> = {
  unit: 1, // pièce (unité mineure = pièce)
  kg: 1_000_000, // poids : unité mineure = milligramme
  m: 1_000, // longueur : unité mineure = millimètre
  L: 1_000, // volume : unité mineure = millilitre
};

// ── toMinor / fromMinor — conversion d'une valeur d'UNITÉ MAJEURE vers/depuis l'unité mineure entière ──
// `toMinor` = frontière d'ENTRÉE (0,35 kg → 350000) : seul endroit où une décimale devient entier (arrondi
// unique). `fromMinor` = frontière de SORTIE/affichage (350000 → 0,35 kg).
export function toMinor(value: number, uom: string): number {
  const scale = UOM_SCALE[uom];
  if (scale === undefined) throw new Error(`core-money: UoM inconnue « ${uom} »`);
  // FAIL-FAST comme le reste du module : NaN/±Infinity (poids vide/mal parsé en amont) LÈVENT au lieu
  // d'empoisonner la quantité en silence (§ audit 2026-07). Seule frontière flottante assumée du module.
  if (!Number.isFinite(value)) throw new Error(`core-money: toMinor attend un nombre FINI (reçu ${value})`);
  // Arrondi half-up SYMÉTRIQUE (0,5 → 1 ; −0,5 → −1). Math.round seul casserait la tie des négatifs
  // vers +∞ (Math.round(−0,5) = 0), en contradiction avec divRoundHalfUp et la symétrie §30.3.
  const p = value * scale;
  return p < 0 ? -Math.round(-p) : Math.round(p);
}
export function fromMinor(minor: number, uom: string): number {
  const scale = UOM_SCALE[uom];
  if (scale === undefined) throw new Error(`core-money: UoM inconnue « ${uom} »`);
  if (!Number.isInteger(minor)) throw new Error(`core-money: fromMinor attend un entier (unité mineure, reçu ${minor})`);
  return minor / scale;
}
